import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getActivitySessionId, useActivityScopeRef, useActivityTracker } from "src/activity/ActivityProvider";
import { streamChatTurn } from "src/advisor/chatStream";

export interface ChatMessage {
	id: string;
	role: "user" | "assistant";
	content: string;
	sentAt: Date;
}

/**
 * One advisor conversation. Grounding happens server-side: the API attaches this visitor's
 * processed activity context to each turn, so the client only sends the question, the page and the scope.
 */
export const useAdvisorChat = () => {
	const { t } = useTranslation();
	const tracker = useActivityTracker();
	const scope = useActivityScopeRef();
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const conversationId = useRef<string | undefined>(undefined);

	const appendAssistantText = useCallback((text: string) => {
		setMessages(current => {
			const last = current.at(-1);
			if (last?.role !== "assistant")
				return [...current, { id: crypto.randomUUID(), role: "assistant", content: text, sentAt: new Date() }];
			return [...current.slice(0, -1), { ...last, content: last.content + text }];
		});
	}, []);

	const send = useCallback(
		async (text: string) => {
			const message = text.trim();
			if (!message || busy) return;
			setError(undefined);
			setBusy(true);
			setMessages(current => [
				...current,
				{ id: crypto.randomUUID(), role: "user", content: message, sentAt: new Date() }
			]);
			try {
				// Ship pending activity first so this turn is grounded in what the user just did.
				await tracker.flush();
				const result = await streamChatTurn(
					{
						conversationId: conversationId.current,
						message,
						sessionId: getActivitySessionId(),
						page: { path: window.location.pathname, title: document.title },
						scope: scope.current
					},
					appendAssistantText
				);
				conversationId.current = result.conversationId;
			} catch {
				setError(t("advisor.error"));
			} finally {
				setBusy(false);
			}
		},
		[appendAssistantText, busy, scope, t, tracker]
	);

	/** "+" in the advisor header: start a new conversation (a new memory thread in the AI infra). */
	const reset = useCallback(() => {
		if (busy) return;
		conversationId.current = undefined;
		setMessages([]);
		setError(undefined);
	}, [busy]);

	return { messages, busy, error, send, reset };
};

export type AdvisorChat = ReturnType<typeof useAdvisorChat>;
